import React, { memo, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaTimes, FaDatabase, FaSpinner, FaBoxOpen } from 'react-icons/fa';
import { Modal } from '@/shared/components/modals';
import { ModelDownloadModalProps } from './types';
import { useModelDownload } from './hooks';
import { formatDate } from './utils';
import { ModelCard } from './ModelCard';
import { ModelCompatibilityBadge } from '../ModelCompatibilityBadge';

/**
 * ModelDownloadModal component
 * Modal for downloading trained models
 */
export const ModelDownloadModal: React.FC<ModelDownloadModalProps> = memo(({
  isOpen,
  onClose,
  sessionId,
  onModelSaved
}) => {
  const { t, i18n } = useTranslation('training');
  const titleId = useId();

  const {
    models,
    loading,
    downloading,
    downloadingAll,
    error,
    handleDownload,
    handleDownloadAll,
    handleSaveToDatabase
  } = useModelDownload(sessionId, isOpen, onClose, onModelSaved);

  // Memoized date formatter with current language
  const formatDateWithLanguage = useCallback((dateString: string) => {
    return formatDate(dateString, i18n.language);
  }, [i18n.language]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleId={titleId}
      size="3xl"
      maxHeightClass="max-h-[85vh]"
      className="ring-1 ring-border/60 animate-slideUp rounded-xl shadow-2xl"
    >
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-5 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shadow-sm">
            <FaDownload className="text-primary-foreground text-sm" />
          </div>
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-foreground tracking-tight">
              {t('training.modelDownload.title')}
            </h2>
            {models.length > 0 && !loading && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('training.modelDownload.fileCount', { count: models.length })}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
          aria-label={t('training.modelDownload.close')}
        >
          <FaTimes size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[55vh] bg-muted/30">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-4">
              <FaSpinner className="animate-spin text-primary text-xl" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">{t('training.modelDownload.loading')}</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200/80 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-500 text-sm font-bold">!</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-red-800 mb-1">
                  {t('training.evaluation.errorTitle')}
                </h3>
                <p className="text-sm text-red-600 leading-relaxed">{error}</p>
              </div>
            </div>
          </div>
        ) : models.length > 0 ? (
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              {t('training.modelDownload.availableModels')}
            </p>
            <div className="space-y-2.5">
              {models.map((model, index) => (
                <ModelCard
                  key={index}
                  model={model}
                  downloading={downloading}
                  downloadingAll={downloadingAll}
                  onDownload={handleDownload}
                  formatDate={formatDateWithLanguage}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
              <FaBoxOpen className="text-muted-foreground text-2xl" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">
              {t('training.modelDownload.noModels')}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 pt-4 pb-4 border-t border-border bg-card">
        <div className="mb-3">
          <ModelCompatibilityBadge />
        </div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FaDatabase className="text-[10px]" />
            <span>{t('training.modelDownload.metadataSaved')}</span>
          </div>
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={handleSaveToDatabase}
              disabled={downloadingAll}
              className="text-sm font-medium text-foreground hover:text-foreground bg-muted hover:bg-muted/80 disabled:opacity-40 px-5 py-2 rounded-lg transition-all duration-150"
            >
              {t('training.modelDownload.close')}
            </button>
            {models.length > 0 && (
              <button
                type="button"
                onClick={handleDownloadAll}
                disabled={downloadingAll || downloading !== null}
                className="text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground px-5 py-2 rounded-lg flex items-center gap-2 transition-all duration-150 hover:shadow-md active:scale-[0.98]"
              >
                {downloadingAll ? (
                  <>
                    <FaSpinner className="animate-spin text-xs" />
                    <span>{t('training.modelDownload.downloadingAll')}</span>
                  </>
                ) : (
                  <>
                    <FaDownload className="text-xs" />
                    <span>{t('training.modelDownload.downloadAll')}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
});

ModelDownloadModal.displayName = 'ModelDownloadModal';

export default ModelDownloadModal;
